/**
 * livePlayTracker.ts — Live-play telemetry finalize hook
 * =====================================================================
 * BETA 15.91 — Per Dylan: "we need normal live plays to submit every
 * time" + "with the exact same data we get from bot runs. and the user
 * who was playing." + "i want all players who ever play... for at least
 * a little bit until we get solid data."
 *
 * Mirrors the bot's RunLog finalize pattern (autoPlaytester.ts:finalizeRun)
 * for live human play. Builds a run object from current GameState at the
 * moment a run ends (death or victory), routes through the existing
 * recordBotRunEvent() pipeline in perfTelemetry.ts. That pipeline now tags
 * the row with category='live_play' (vs 'bot_run') and stuffs the player's
 * username into the reporter field. Same column shape as bot_run rows so
 * PSBOT RUN's existing balance queries work unchanged.
 *
 * NO GATE — per Dylan, every player submits until we have solid data. Add
 * a Settings opt-out toggle later if user pushback or compliance requires.
 *
 * IMPLEMENTATION NOTE: Rather than instrument engine.ts with per-frame
 * trackers (which would touch dozens of code paths and risk perf
 * regressions), we read everything possible from state + module
 * singletons at finalize time. Some bot-only RunLog fields can't be
 * derived this way (decisionTraceJson, pickAuditJson with full delta
 * history, etc.) — those fields are simply omitted. PSBOT RUN's queries
 * are field-tolerant — missing fields = NULL in the JSON column, doesn't
 * break aggregation.
 * =====================================================================
 */

import type { GameState } from './types';
import { GAME_VERSION } from './engine';
import { recordBotRunEvent } from '../lib/perfTelemetry';
import { getWeaponSystemState } from './weaponAdapter';
// BETA 22.47 — new-system telemetry sources (dash / mods / Cloakzy / extraction stash).
import { getArmedWeapons } from './weaponArmed';
import { getEquipped } from './weaponModLoadout';
import { getInv as getStarforgeInv } from './starforgeInventory';
import { getItemState, getPercState, getAppliedBuffsList, getShrineDifficultyPct } from './itemsAdapter';
import { getWaveSystemState } from './enemyAdapter'; // BETA 20.39 — swarm count for live payload
// BETA 17.06 — PSBOT cycle-30v parity: live_play needs the ground-effect
// spawn counts (already wired in autoPlaytester via the shared tracker
// engine.ts:326 registers). Re-exporting under a private name so the
// finalize path can read it without a dynamic import (async-context bug).
import { getLocalFloorEffectTracker as _getLocalFloorEffectTrackerForLivePlay } from './weaponLab';
// BETA 17.07 — Karen Drone diagnostic counter (PSBOT cycle-30w Protocol B).
import { getKarenDroneTrace as _getKarenDroneTraceForLivePlay } from './weaponSystem';
// BETA 17.08 — PSBOT cycle-30t Tier C closing batch (6 final counters).
// Reads from shared trackers + 2 new state fields wired this beta.
import { getLocalItemTracker as _getLocalItemTrackerForLive } from './weaponLab';
import { getLocalDeathContext as _getDeathContextForLive } from './weaponLab'; // BETA 20.86 — true fatal source for audit
import { isBotV2Active as _isBotV2ActiveForLive } from './autoPlaytester_v2'; // BETA 20.90 — v2 bot cohort stamp
import { getRollSeed as _getRollSeedForLive } from './rng'; // BETA 20.92 — reproducible-run seed
import { getAnusRocketsFiredThisRun as _getAnusRocketsForLive } from './itemEffectSystem';
import { getSillyJuicePeakStack as _getSillyJuicePeakForLive } from './itemsAdapter';
// BETA 16.29 — PSBOT cycle-30f schema parity: pull character passive +
// signature accumulators so live_play can write the same fields bot_run
// already does. Module exports get-functions per character (Sponge,
// Killa, Cowboy, etc).
import {
  getPassiveBonusFinalThisRun,
  getSignatureProcsThisRun,
  getSignatureMaxStacksThisRun,
  getSignatureActiveSecondsThisRun,
  getSignatureBonusDamageThisRun,
} from './characterPassives';
// BETA 16.06 — sessionTracker holds the username set via startSession()
// on game-mount. Was previously reading from localStorage which didn't
// contain the username — caused 'live-play:anonymous' on every row.
import { getSessionData } from './sessionTracker';
// BETA 16.70 — Mobile telemetry tagging on every live_play row.
import { classifyDevice, isTouchDevice, isStandalonePWA, isIPhoneSafari, getNetworkClass } from './deviceDetect';
// BETA 16.71 — Cross-platform tracking: input mix + controller + perf.
import { getInputMixSnapshot } from './inputTracker';
import { getConnectedControllerName } from './gamepadInput';
import { getPerfRunSnapshot } from './performanceTracker';

/**
 * BETA 16.20 — PSBOT cycle-23 P1.5: per-pick event log. Lightweight schema
 * vs the bot's full pickAuditJson (which carries before/after stat
 * snapshots). Just enough to answer:
 *   - Which percs/items got picked, and how often?
 *   - At what time / level / source (level-up vs chest_paid vs chest_free
 *     vs shrine vs ultra_chest)?
 *   - What rarity did each pick land at? (correlate with luck stat)
 *
 * Schema per entry:
 *   { t: gameTimeSec, source: 'levelup' | 'chest_paid' | ..., pickedId: 'T01',
 *     rarity?: 'common' | 'rare' | 'epic' | 'legendary', stat?: 'damage',
 *     delta?: number }
 *
 * Pushed by recordLivePickEvent (called from itemsAdapter pick handlers).
 * Reset on initGame via resetLivePlayTracker.
 */
export interface LivePickEvent {
  t: number;
  source: 'levelup' | 'chest_paid' | 'chest_free' | 'shrine' | 'ultra_chest';
  pickedId: string;
  // BETA 22.10 — the FULL set of option IDs offered this pick (not just the chosen one), so
  // offered-variety is measurable run-to-run (offered-distinct-ratio per system). Dylan's
  // "every run feels the same" needed this — pickedId alone can't show whether OFFERS repeat.
  offered?: string[];
  rarity?: string;
  stat?: string;
  delta?: number;
  // BETA 16.21 — PSBOT cycle-24 P1.1+P1.3: stat-application investigation.
  // Snapshot of the player's COMPUTED stat fields (post applyAllBonusesToPlayer)
  // at the moment of pick. PSBOT can diff consecutive events to verify
  // whether a T02 pick actually moves attackSpeedBonus, or T03 moves maxHp.
  // If you see two consecutive T02 picks where attackSpeedBonus stays flat,
  // that's a stat-application bug. statsAtPick is captured BEFORE the pick
  // applies (since the recompute lands on the next frame).
  statsAtPick?: {
    maxHp: number;
    hp: number;
    damageBonus: number;
    attackSpeedBonus: number;
    critChance: number;
    critDamage: number;
    lifesteal: number;
    eliteDamageBonus: number;
    armor: number;
    luck: number;
    // BETA 19.93 — Dylan: "are perks working correctly... did this pick
    // actually change anything." Original 10-field snapshot couldn't show
    // delta on projectile-count / knockback / phase / regen picks. Now
    // captures every numeric player stat so before/after-pick diffs work
    // for ALL perc/shrine/item targets.
    projectileCountBonus: number;
    projectileBounceBonus: number;
    knockbackBonus: number;
    pickupRange: number;
    shield: number;
    maxShield: number;
    evasion: number;
    thorns: number;
    hpRegen: number;
    shieldRegen: number;
    contactPhaseChance: number;
    xpMultiplier: number;
    goldMultiplier: number;
    attackAreaBonus: number;
    projectileSpeedBonus: number;
    overheal: number;
    speed: number;
  };
}

let _pickEvents: LivePickEvent[] = [];

/**
 * BETA 16.26 — PSBOT cycle-27 URGENT: bot_run schema catch-up. Exposes a
 * snapshot of the per-pick event log so the bot finalizer (autoPlaytester
 * .finalizeRun) can write the same `pickEvents` field that live_play
 * rows already have. Returns a defensive copy.
 *
 * Both bot AND human runs go through the same `handleLevelUpSelection`
 * / `handleShrineSelection` / `handleChestItemSelection` paths in
 * itemsAdapter.ts, which already call `recordLivePickEvent()`. So the
 * `_pickEvents` buffer is populated for BOTH cohorts — bot just hasn't
 * been reading it at finalize time. Fixed by this getter.
 */
export function getLivePlayPickEvents(): LivePickEvent[] {
  return _pickEvents.slice();
}

/**
 * BETA 16.26 — PSBOT cycle-27 URGENT shared finalize helper. Returns the
 * 4 missing-from-bot final-stat fields + finalMaxHpDebug + pickEvents.
 * Mirrors the logic in `buildLiveRun` so both cohorts (bot_run +
 * live_play) carry identical schema. Safe to call from any finalize
 * path that has access to `state`.
 */
export interface ExtendedTelemetryFields {
  pickEvents: LivePickEvent[];
  finalShield: number;
  finalEvasionPct: number;
  finalMoveSpeedPct: number;
  // BETA 16.55 — PSBOT cycle-30m Phase 3: surface raw player speed in
  // wu/sec so PSBOT can verify cap removal in cycle-31. Pre-16.55 had
  // a hard cap at 400 (Math.min in itemsAdapter.ts:1624) which clipped
  // every speed build at +100% bonus. Now uncapped — finalRawSpeed
  // should rise above 400 for fast-build runs.
  finalRawSpeed: number;             // raw wu/sec at finalize
  finalSpeedBonusPct: number;        // (raw - 200) / 200 × 100, % above base
  // BETA 16.36 — shield regen tracking (rate + run totals).
  // Dylan: "add all shield regen tracking ad upgrades in bot runs and
  // regular plays."
  finalShieldRegen: number;            // /sec rate at end of run
  runShieldDamageAbsorbed: number;     // total shield-absorbed damage this run
  runShieldRegenerated: number;        // total shield regenerated this run
  // BETA 16.37 — PSBOT cycle-30i §7E Bug C fix: lifesteal + Mom's Cooking
  // healing now feeds this counter. Was always 0 in prior telemetry.
  runHealingReceived: number;          // total HP healed this run (lifesteal + active heals)
  // BETA 16.37 — phase time tracking (PSBOT cycle-30i §7E Bug F)
  phaseTimePlaying: number;
  phaseTimeLevelup: number;
  phaseTimeChest: number;
  phaseTimeShrine: number;
  // BETA 22.47 — NEW-SYSTEM telemetry (dash, weapon mods, Cloakzy, extraction stash).
  // Per Dylan: "we have a lot of new things... need to be updating tracking" + the
  // bots must learn about all of it. Computed from run-scoped state counters +
  // the persistent mod/inventory stores at finalize. Identical for live + bot rows.
  dashesUsed: number;                 // dash bursts fired this run
  cloakzyDeals: number;               // times the player gambled with Cloakzy
  cloakzyGoldSpent: number;           // total gold Cloakzy snatched this run
  modsSocketedAtEnd: number;          // mods on armed gun(s) at finalize (= lost on death, kept on extract)
  stashScrap: number;                 // scrap held in the Starforge stash
  stashCases: number;                 // un-opened Starforge cases held
  finalMaxHpDebug: {
    playerMaxHp: number;
    playerHp: number;
    itemMaxHpStat: number | null;
    itemBaseMaxHp: number | null;
    percMaxHpAccum: number;
    characterId: string;
    suspectItems: string[];
  };
}
export function buildExtendedTelemetryFields(state: GameState): ExtendedTelemetryFields {
  const p = state.player;
  const itemState = getItemState();
  const percState = getPercState();
  let percMaxHpAccum = 0;
  if (percState?.slots) {
    for (const slot of percState.slots) {
      if (slot.perc && slot.perc.stat === 'maxHp' && slot.level > 0) {
        percMaxHpAccum += slot.accumulatedBonus;
      }
    }
  }
  // BETA 16.36 — pull shield-regen tracking off lazy state fields.
  const sAny = state as unknown as Record<string, number | undefined>;
  return {
    pickEvents: _pickEvents.slice(),
    finalShield: Math.round(p?.maxShield ?? 0),
    finalEvasionPct: Math.round((p?.evasion ?? 0) * 100) / 100,
    finalShieldRegen: Math.round(((p as unknown as { shieldRegen?: number })?.shieldRegen ?? 0) * 10) / 10,
    runShieldDamageAbsorbed: Math.round(sAny._shieldDamageAbsorbedThisRun ?? 0),
    runShieldRegenerated: Math.round(sAny._shieldRegeneratedThisRun ?? 0),
    runHealingReceived: Math.round(sAny._healingReceivedThisRun ?? 0),
    phaseTimePlaying: Math.round((sAny._phaseTimePlaying ?? 0) * 10) / 10,
    phaseTimeLevelup: Math.round((sAny._phaseTimeLevelup ?? 0) * 10) / 10,
    phaseTimeChest: Math.round((sAny._phaseTimeChest ?? 0) * 10) / 10,
    phaseTimeShrine: Math.round((sAny._phaseTimeShrine ?? 0) * 10) / 10,
    // BETA 22.47 — new-system telemetry. Run-scoped counters off state + the
    // persistent mod/inventory stores (read at finalize, BEFORE the death-strip
    // runs — so modsSocketedAtEnd reflects what was on the gun at death).
    dashesUsed: Math.round(sAny._dashesUsedThisRun ?? 0),
    cloakzyDeals: Math.round(sAny._cloakzyDealsThisRun ?? 0),
    cloakzyGoldSpent: Math.round(sAny._cloakzyGoldSpentThisRun ?? 0),
    modsSocketedAtEnd: (() => {
      try { let n = 0; for (const w of getArmedWeapons()) n += getEquipped(w).length; return n; } catch { return 0; }
    })(),
    stashScrap: (() => { try { return Math.round(getStarforgeInv().scrap ?? 0); } catch { return 0; } })(),
    stashCases: (() => { try { return getStarforgeInv().cases?.length ?? 0; } catch { return 0; } })(),
    // BETA 16.27 — PSBOT cycle-30b Bug 3 FIX. Was reading raw p.speed
    // (200 base, 200-400 with bonuses), which read as "200%" in PSBOT
    // data even with shrines applied. The field is named *Pct so it
    // should be the BONUS percent above base, not the raw speed value.
    // Now: ((speed - 200) / 200) × 100 = % bonus above 200 base.
    finalMoveSpeedPct: Math.round(((p?.speed ?? 200) - 200) / 200 * 100 * 100) / 100,
    // BETA 16.55 — PSBOT cycle-30m: raw wu/sec + bonus%, separate fields
    // so cycle-31 can verify cap removal landed.
    finalRawSpeed: Math.round((p?.speed ?? 200) * 10) / 10,
    finalSpeedBonusPct: Math.round(((p?.speed ?? 200) - 200) / 200 * 100 * 10) / 10,
    finalMaxHpDebug: {
      playerMaxHp: Math.round(p?.maxHp ?? 0),
      playerHp: Math.round(p?.hp ?? 0),
      itemMaxHpStat: itemState?.playerStats?.maxHp ?? null,
      itemBaseMaxHp: itemState?.playerStats?.baseMaxHp ?? null,
      percMaxHpAccum: Math.round(percMaxHpAccum * 100) / 100,
      characterId: state.character?.id ?? 'unknown',
      suspectItems: itemState?.inventory
        ?.filter(s => /iranian|sheeesh|gas_mask|deck/.test(s.item.id))
        .map(s => `${s.item.id}x${s.stacks}`) ?? [],
    },
  };
}

// BETA 16.24 — PSBOT cycle-25 P0.S2: snapshot the player's HP AT THE
// LETHAL DAMAGE MOMENT, not at finalize time. With the 16.21 death
// replay slow-mo (5s of game time still ticking), regen/lifesteal/
// VooGoo/Mom's Cooking can heal the dead player back up before
// buildLiveRun reads state.player.hp — produced the cycle-25 audit
// finding "hpAtDeath: 129 with a lying deathCause" (HP shouldn't
// be 129 of 150 if the boss killed you). Engine sets this in the lethal
// branch of player-death detection. Reset on resetLivePlayTracker.
let _capturedHpAtDeath: number | null = null;
export function captureLiveHpAtDeath(hp: number): void {
  // Only capture the FIRST lethal moment per run (so revive paths that
  // also pass through this branch don't overwrite the real death HP).
  if (_capturedHpAtDeath === null) {
    _capturedHpAtDeath = Math.max(0, Math.round(hp));
  }
}

// BETA 16.21 — PSBOT cycle-24 P1.1+P1.3: optional stats provider so
// recordLivePickEvent can auto-snapshot the live player's computed stats
// without each pick path having to plumb player state. Engine.ts registers
// this at module init, mirrors registerShrineSelectTracker pattern.
type LivePlayerStatsProvider = () => LivePickEvent['statsAtPick'] | null;
let _livePlayerStatsProvider: LivePlayerStatsProvider | null = null;
export function registerLivePlayerStatsProvider(provider: LivePlayerStatsProvider | null): void {
  _livePlayerStatsProvider = provider;
}

export function recordLivePickEvent(ev: LivePickEvent): void {
  // Cap at 200 entries per run as a safety bound — typical long run has
  // 50-80 picks. 200 entries × ~80 bytes ≈ 16 KB, well under the 100 KB
  // Supabase row cap.
  if (_pickEvents.length >= 200) return;
  // BETA 16.21 — PSBOT cycle-24 P1.1+P1.3: auto-fill statsAtPick if a
  // provider is registered and caller didn't supply one explicitly.
  if (!ev.statsAtPick && _livePlayerStatsProvider) {
    try {
      const snap = _livePlayerStatsProvider();
      if (snap) ev.statsAtPick = snap;
    } catch { /* never crash on snapshot */ }
  }
  _pickEvents.push(ev);
}

/**
 * Outcome of a live-play run for the run object's `deathCause` semantic.
 * 'gameover' = killed by enemy, 'won' = beat the final boss, 'quit' = player
 * clicked QUIT or pressed Esc to leave the run early.
 *
 * BETA 16.03 — Per Dylan: "you should have added the stuff in supa base to
 * track these player runs." Was: only natural deaths/wins submitted to
 * Supabase. Players who quit a run mid-play (or who couldn\'t die for any
 * reason and quit out of frustration — like Dylan today) produced ZERO
 * telemetry rows. Now QUIT is captured too with deathCause='quit'.
 */
type LiveOutcome = 'gameover' | 'won' | 'quit' | 'extracted';

/**
 * BETA 16.06 — Read the player's username from sessionTracker (set on
 * game startSession in GameCanvas). Was reading localStorage('goopzone_goop_system')
 * but the goop save doesn't contain a username field — landed every
 * Supabase row as 'live-play:anonymous'.
 */
function readUsername(): string {
  try {
    const u = getSessionData()?.username;
    return (u && typeof u === 'string') ? u : '';
  } catch {
    return '';
  }
}

/**
 * Build a flat object whose field NAMES match the bot's RunLog shape so
 * PSBOT RUN can run identical balance queries against live_play rows.
 * Returns a plain object — recordBotRunEvent stringifies + inserts.
 */
export function buildLiveRun(state: GameState, outcome: LiveOutcome): Record<string, unknown> {
  const run: Record<string, unknown> = {};

  // ── Identity ──
  run.gameVersion = GAME_VERSION;
  run.username = readUsername();
  run.characterId = state.character?.id ?? 'unknown';
  run.characterName = state.character?.name ?? 'unknown';
  run.runNumber = Date.now(); // wall-clock as a unique key for live runs (bot uses a counter)
  // BETA 20.67 — cohort integrity: autoPlaytester marks _botEnabledRun on the
  // live state every bot tick; perfTelemetry routes any botEnabled payload to
  // category bot_run even without ?botfarm=true (was contaminating live_play).
  // BETA 20.90 — PSBOT cycle-30bh #2: the v2 farm never sets _botEnabledRun, so
  // botEnabled was null on every v2 row (unusable as a "this was a bot" filter).
  // OR in isBotV2Active() so v2 runs stamp true too.
  run.botEnabled = ((state as unknown as { _botEnabledRun?: boolean })._botEnabledRun === true) || _isBotV2ActiveForLive();
  // BETA 16.84 — Per PSBOT cycle-30o: "startTime preservation fix."
  // Was: read `_runStartTime` (an underscore-prefixed field that NO
  // engine code ever writes) → always undefined → fell back to
  // Date.now() which is the SAME timestamp as run.endTime on the
  // next line → wallClockSec computed as 0 for every run.
  // Fix: read state.startTime instead — that's the actual field set
  // at game init in engine.ts:948 (Date.now()) and never overwritten
  // mid-run. wallClockSec now reflects real wall-clock elapsed time.
  run.startTime = (state as unknown as { startTime?: number }).startTime ?? Date.now();
  run.endTime = Date.now();
  run.timeScale = (state as unknown as { debugTimeScale?: number }).debugTimeScale ?? 1;

  // ── Outcome ──
  // deathCause matches the bot format: 'enemy:<type>' / 'boss:<name>' /
  // 'won' / 'quit' (BETA 16.03 — quit added so player-quit runs land in
  // Supabase too, not just natural deaths).
  if (outcome === 'won') {
    run.deathCause = 'won';
  } else if (outcome === 'extracted') {
    run.deathCause = 'extracted'; // BETA 22.21 — survived the 60s extraction timer (kept loot)
  } else if (outcome === 'quit') {
    run.deathCause = 'quit';
  } else {
    const killedBy = state.killedByName ?? state.killedByType ?? 'unknown';
    const variant = state.killedByVariant;
    run.deathCause = variant === 'boss' ? `boss:${killedBy}` : `enemy:${killedBy}`;
    // BETA 20.86 — surface the TRUE fatal source + last-5-hits so a future audit
    // can VERIFY deathCause matches what actually killed the player (Dylan: "i
    // feel like its wrong on what killed me most of the time"). This was computed
    // into deathContext every run but never emitted to the row — run a7c89e26 had
    // none of it. Now the killer is auditable end-to-end.
    const _dc = _getDeathContextForLive();
    if (_dc) {
      const _r = run as unknown as Record<string, unknown>;
      _r.fatalHitSource = _dc.fatalHitSource;
      _r.fatalHitDmg = _dc.fatalHitDmg;
      _r.last5HitsBeforeDeath = _dc.last5HitsBeforeDeath;
      _r.distanceToNearestEnemyAtDeath = Math.round(_dc.distanceToNearestEnemy);
      _r.enemiesInContactRangeAtDeath = _dc.enemiesInContactRangeAtDeath;
      // BETA 22.48 — death clarity: how long before death the last hit landed +
      // whether it was a "phantom" death (no fresh enemy strike). Lets us audit
      // EVERY death for "was I actually supposed to die" across the run history.
      _r.fatalHitAgeSec = _dc.fatalHitAgeSec ?? -1;
      _r.deathWasPhantom = _dc.deathWasPhantom ?? false;
      _r.deathExplanation = state.deathExplanation ?? '';
    }
  }
  run.survivalTimeSec = Math.round((state.time ?? 0) * 100) / 100;
  run.wallClockSec = Math.round(((Date.now() - (run.startTime as number)) / 1000) * 100) / 100;
  run.floorReached = state.wave ?? 1;
  // BETA 20.92 — record the balance-roll seed so any run replays its EXACT
  // level-up offers/magnitudes (set window.__setRunSeed(n) before a run to fix it).
  (run as unknown as Record<string, number>).runSeed = _getRollSeedForLive();
  run.waveReached = state.wave ?? 1;

  // ── Combat tallies ──
  run.killCount = state.killCount ?? 0;
  // BETA 20.44 — meta-currency goop earned this run (mirrors Home.tsx:1068 =
  // max(10, kills×2)). Surfaced so the payload has a row for it (deep-audit gap).
  (run as unknown as Record<string, number>).goopEarnedThisRun = Math.max(10, Math.floor((state.killCount ?? 0) * 2));
  // BETA 20.45 — HIGH-FREQUENCY HUMAN PLAY TRACE (Dylan: precise bot-training +
  // error-pinpointing data). Compact flat-array rows sampled ~3×/sec in the engine;
  // humanTraceCols documents the column order so the analyst/bot-trainer can parse.
  {
    const _trace = (state as unknown as { _humanTrace?: number[][] })._humanTrace ?? [];
    (run as unknown as Record<string, unknown>).humanTrace = _trace;
    // BETA 20.46 — frameMs is now the REAL full-frame time (actual fps); updateMs
    // is engine-logic-only. render/browser cost ≈ frameMs - updateMs.
    (run as unknown as Record<string, unknown>).humanTraceCols = ['t','px','py','vx','vy','hp','alive','nearestEnemyDist','playerProjAlive','frameMs','kills','updateMs'];
    (run as unknown as Record<string, number>).humanTraceCount = _trace.length;
  }
  run.damageTaken = Math.round(state.damageTakenThisRun ?? 0);
  // BETA 20.39 — was reading dead `_runHealingReceived` (never written anywhere
  // → always 0). Now reads the real accumulator `_healingReceivedThisRun` so
  // this field matches `runHealingReceived` instead of a misleading zero.
  run.healingReceived = Math.round((state as unknown as { _healingReceivedThisRun?: number })._healingReceivedThisRun ?? 0);
  // BETA 19.99 — was state.player?.gold (CURRENT wallet, decremented by Debt
  // drain + chest purchases). Now uses the lifetime accumulator
  // _goldCollectedThisRun which is incremented at every player.gold +=
  // site (orbs, boss drops, chat tips). Falls back to player.gold if the
  // accumulator hasn't been written yet (legacy data).
  {
    const _gSAny = state as unknown as Record<string, number | undefined>;
    run.goldCollected = _gSAny._goldCollectedThisRun ?? state.player?.gold ?? 0;
    // BETA 21.42 — surface orbs collected this run (magnet / pickup-range
    // effectiveness was previously invisible — see engine.ts accumulator).
    (run as unknown as Record<string, number>).orbsCollected = _gSAny._orbsCollectedThisRun ?? 0;
  }
  run.chestsOpened = state.chestsOpenedThisRun ?? 0;
  // BETA 16.90 — PSBOT cycle-30f Bug 6: surface shrinesActivatedThisRun
  // (was always 0 in live_play even though shrine pickEvents fired).
  run.shrinesActivated = state.shrinesActivatedThisRun ?? 0;
  // BETA 20.43 — the actual shrine buffs applied (not just the count), so the
  // owner can verify each shrine blessing landed. getAppliedBuffsList() returns
  // [{stat,value,pct,label}] accumulated as shrines were chosen this run.
  try { (run as unknown as Record<string, unknown>).shrineBuffsApplied = getAppliedBuffsList(); } catch { /* ignore */ }

  // BETA 20.02 — Surface engine frame-perf to live-play payload (was only
  // in bot CSV via autoPlaytester:7391). Dylan: "is there anything tracking
  // these lag spikes?" — yes, but it wasn't being sent up. Now every
  // live-play row includes worstFrameTimeMs, avgFrameTimeMs, and the array
  // of spike timestamps (capped at 50 per run). Lets me diagnose exact
  // moments where his game stuttered.
  {
    const _perfAny = state as unknown as {
      _engineFramePerfThisRun?: { spikes?: Array<{ t: number; frameTimeMs: number }>; avgFrameTimeMs?: number; worstFrameTimeMs?: number };
    };
    const perf = _perfAny._engineFramePerfThisRun;
    if (perf) {
      (run as unknown as Record<string, unknown>).engineFramePerf = {
        spikes: perf.spikes ?? [],
        avgFrameTimeMs: Math.round((perf.avgFrameTimeMs ?? 0) * 100) / 100,
        worstFrameTimeMs: Math.round((perf.worstFrameTimeMs ?? 0) * 100) / 100,
        spikeCount: (perf.spikes ?? []).length,
      };
    }
  }

  // ── Player position + HP at end ──
  run.deathPosition = { x: Math.round(state.player?.x ?? 0), y: Math.round(state.player?.y ?? 0) };
  // BETA 16.24 — PSBOT cycle-25 P0.S2: prefer the captured-at-lethal-moment
  // snapshot. Fall back to live state.player.hp only if capture didn't
  // fire (e.g. won/quit outcomes which never go through the lethal branch).
  run.hpAtDeath = _capturedHpAtDeath !== null
    ? _capturedHpAtDeath
    : Math.round(state.player?.hp ?? 0);
  run.levelReached = state.player?.level ?? 1;

  // ── Final stats snapshot (matches RunLog finalDamagePct etc. names) ──
  // Most stats are stored on player as raw numbers; the bot CSV expresses
  // some as % values. We mirror that here (multiply ratio fields by 100
  // when the field name ends in Pct).
  const p = state.player;
  if (p) {
    run.finalMaxHP = Math.round(p.maxHp);
    run.finalDamagePct = Math.round((p.damageBonus ?? 0) * 100) / 100;
    run.finalAttackSpeedPct = Math.round((p.attackSpeedBonus ?? 0) * 100) / 100;
    run.finalCritChancePct = Math.round((p.critChance ?? 0) * 100) / 100;
    run.finalCritDamagePct = Math.round((p.critDamage ?? 0) * 100) / 100;
    run.finalLifestealPct = Math.round((p.lifesteal ?? 0) * 100) / 100;
    run.finalArmor = Math.round(p.armor ?? 0);
    run.finalShield = Math.round(p.maxShield ?? 0);
    run.finalThornsPct = Math.round((p.thorns ?? 0) * 100) / 100;
    run.finalEvasionPct = Math.round((p.evasion ?? 0) * 100) / 100;
    run.finalLuckPct = Math.round((p.luck ?? 0) * 100) / 100;
    run.finalEliteDamagePct = Math.round((p.eliteDamageBonus ?? 0) * 100) / 100;
    run.finalProjectileCount = p.projectileCountBonus ?? 0;
    // BETA 16.27 — PSBOT cycle-30b Bug 3: bonus-percent above 200 base.
    run.finalMoveSpeedPct = Math.round(((p.speed ?? 200) - 200) / 200 * 100 * 100) / 100;
    // BETA 17.07 — PSBOT cycle-30v #3+#4 schema parity (Protocol A).
    // finalRawSpeed + finalSpeedBonusPct were claimed shipped BETA 16.55
    // to bot CSV but never landed in live_play finalize. 4 audits flagged
    // "NOT IN SCHEMA". Fixing here so PSBOT can analyze move-speed across
    // live_play rows going forward.
    const _runForSpeed = run as unknown as Record<string, number>;
    _runForSpeed.finalRawSpeed = Math.round((p.speed ?? 200) * 10) / 10;
    _runForSpeed.finalSpeedBonusPct = Math.round(((p.speed ?? 200) - 200) / 200 * 100 * 10) / 10;

    // BETA 16.29 — PSBOT cycle-30f schema parity: anything bot_run tracks
    // that's NOT bot-AI-specific should also land in live_play. Per Dylan:
    // "anything bot run tracking gets if its not to do with how the bots
    // run or choose items, and it needs to be added to play tracking for
    // when any player plays the game regularly."
    //
    // Final-stat fields previously bot-only (now in live_play too):
    const pAny = p as unknown as { overheal?: number; projectilePierceBonus?: number };
    run.finalOverheal = pAny.overheal ?? 0;
    run.finalProjectilePierce = pAny.projectilePierceBonus ?? 0;
    run.finalProjectileBounce = p.projectileBounceBonus ?? 0;
    run.finalPickupRangePct = p.pickupRange ?? 0;
    // PhaseChancePct: contactPhaseChance is fraction (0-0.95). Field name
    // is *Pct so multiply ×100 for percent semantic (matches the cycle-30b
    // Bug 2 fix on bot side).
    run.finalPhaseChancePct = Math.round((p.contactPhaseChance ?? 0) * 100 * 100) / 100;
    // Knockback bonus + projectileSpeed + attackArea — full Megabonk parity
    run.finalKnockbackPct = Math.round((p.knockbackBonus ?? 0) * 100) / 100;
    run.finalProjectileSpeedPct = Math.round((p.projectileSpeedBonus ?? 0) * 100) / 100;
    run.finalAttackAreaPct = Math.round((p.attackAreaBonus ?? 0) * 100) / 100;
    run.finalHpRegen = Math.round((p.hpRegen ?? 0) * 10) / 10;
    // BETA 16.36 — Shield regen telemetry (Dylan: "add all shield regen
    // tracking ad upgrades in bot runs and regular plays"). Lazy fields
    // on RunLog cast (matches finalShield/finalEvasionPct pattern).
    const _runAny = run as unknown as Record<string, number>;
    _runAny.finalShieldRegen = Math.round(((p as unknown as { shieldRegen?: number }).shieldRegen ?? 0) * 10) / 10;
    // BETA 20.43 — buffable stats that had NO telemetry field (deep-audit gap).
    // damageReduction lives on itemState (item C01 / Mystery Meat Stew etc.);
    // rangeBonus from range shrines/items; difficulty% from negative shrines.
    _runAny.finalDamageReduction = Math.round((getItemState()?.playerStats as { damageReduction?: number } | undefined)?.damageReduction ?? 0);
    _runAny.finalRangeBonus      = Math.round((p as unknown as { rangeBonus?: number }).rangeBonus ?? 0);
    _runAny.finalDifficultyPct   = Math.round(getShrineDifficultyPct());
    const _sAnyShield = state as unknown as Record<string, number | undefined>;
    _runAny.runShieldDamageAbsorbed = Math.round(_sAnyShield._shieldDamageAbsorbedThisRun ?? 0);
    _runAny.runShieldRegenerated = Math.round(_sAnyShield._shieldRegeneratedThisRun ?? 0);
    _runAny.runHealingReceived = Math.round(_sAnyShield._healingReceivedThisRun ?? 0);
    _runAny.phaseTimePlaying = Math.round((_sAnyShield._phaseTimePlaying ?? 0) * 10) / 10;
    _runAny.phaseTimeLevelup = Math.round((_sAnyShield._phaseTimeLevelup ?? 0) * 10) / 10;
    _runAny.phaseTimeChest   = Math.round((_sAnyShield._phaseTimeChest   ?? 0) * 10) / 10;
    _runAny.phaseTimeShrine  = Math.round((_sAnyShield._phaseTimeShrine  ?? 0) * 10) / 10;
    // BETA 16.51 — Last Stand split from item-revives (PSBOT cycle-30k P1)
    _runAny.lastStandTriggersThisRun = _sAnyShield._lastStandTriggersThisRun ?? 0;
    // BETA 16.90 — PSBOT cycle-30k §11B + cycle-30l §4 + cycle-30o:
    // surface item-trigger counters that previously read 0 / None in
    // live_play telemetry even though the engine tracked them. PSBOT
    // analysis pipeline uses these to verify item-trigger reliability
    // across runs (e.g. did the player actually proc Debt? Sheeesh?).
    _runAny.debtSavesTriggered      = _sAnyShield._debtSavesTriggeredThisRun ?? 0;
    _runAny.debtGoldSpent           = _sAnyShield._debtGoldSpentThisRun ?? 0;
    _runAny.debtHitsAbsorbed        = _sAnyShield._debtHitsAbsorbedThisRun ?? 0;
    _runAny.debtForceEnded          = _sAnyShield._debtForceEndedThisRun ?? 0;
    _runAny.perkBungulatorsSpawned  = _sAnyShield._perkBungulatorsSpawnedThisRun ?? 0;
    // BETA 17.07 — PSBOT cycle-30t Tier C counters (5 of 12 shipped this beta).
    // voogoo: heal trigger count + total heal amount summed
    // timmy:  total dmg reduced by item_timmy_repellent_spray
    // last resort: entries-into-low-HP window (sticky-flag edge count)
    // notXanax: count of slow applications (was bot-only, now live too)
    _runAny.voogooHealsTriggered    = _sAnyShield._voogooHealsTriggeredThisRun ?? 0;
    _runAny.voogooHealAmount        = Math.round(_sAnyShield._voogooHealAmountThisRun ?? 0);
    _runAny.timmyDamageReduced      = Math.round(_sAnyShield._timmyDamageReducedThisRun ?? 0);
    _runAny.lastResortActivations   = _sAnyShield._lastResortActivationsThisRun ?? 0;
    _runAny.notXanaxSlowsApplied    = _sAnyShield._notXanaxSlowsAppliedThisRun ?? 0;
    // BETA 17.11 — A3 + A7 proof counters (PSBOT keeps claiming these aren't
    // shipped; now they get firing-count fields in CSV).
    _runAny.a3InvulnRegenPausedFrames = _sAnyShield._a3InvulnRegenPausedFramesThisRun ?? 0;
    _runAny.a7TimmySlowsApplied       = _sAnyShield._a7TimmySlowsAppliedThisRun ?? 0;
    // BETA 17.12 — Floor 1 progression diagnostic (PSBOT cycle-30ad Bundle 3).
    _runAny.megaBossKilled    = _sAnyShield._megaBossKilledThisRun ?? 0;
    _runAny.bossEliteKills    = _sAnyShield._bossEliteKillsThisRun ?? 0; // BETA 20.40 — elite+boss variant kills (was only mega)
    _runAny.enemyDespawnsNoDeath = (_sAnyShield as unknown as Record<string, number>)._enemyDespawnsNoDeathThisRun ?? 0; // BETA 20.58 — enemies removed without dying (vanishing-elite tracker)
    _runAny.enemyAttacksAbsorbedByDebt = (_sAnyShield as unknown as Record<string, number>)._enemyAttacksAbsorbedByDebtThisRun ?? 0; // BETA 20.67 — closes the ledger: attempted = hit + missed* + absorbedByDebt
    _runAny.runRegenHealed = Math.round((_sAnyShield as unknown as Record<string, number>)._regenHealedThisRun ?? 0); // BETA 20.67 — passive-regen share of runHealingReceived (Charter §2)
    _runAny.eliteChestsDropped = (_sAnyShield as unknown as Record<string, number>)._eliteChestsDroppedThisRun ?? 0; // BETA 20.67 — chests DROPPED, not just opened (Charter §4 beats)
    _runAny.portalReached     = _sAnyShield._portalReachedThisRun ? 1 : 0;
    _runAny.floorAdvances     = _sAnyShield._floorAdvancesThisRun ?? 0;
    // BETA 20.39 — swarm count (was bot-only) + Ultra item telemetry. Dylan
    // deep-audit: the 7 ultras fired with ZERO counters; "everything needs a
    // row." Read per-item activity from the ultraState bag at finalize. NOTE:
    // ultra_1000lb_momentum is fully wired since BETA 20.41 (kill 5 in 3s → a ~1s
    // surge of 3× damage + pierce-all); ultraMomentumProcs increments per charge.
    {
      const _wave = getWaveSystemState();
      _runAny.totalSwarms = _wave?.totalSwarms ?? 0;
      const _ultra = (state.player?.ultraState ?? {}) as Record<string, unknown>;
      const _activeUltras = state.player?.activeUltraItems ?? [];
      const _num = (v: unknown) => (typeof v === 'number' ? v : 0);
      _runAny.ultrasOwnedCount      = _activeUltras.length;
      _runAny.ultraGrindStacks      = _num(_ultra.grind_stacks);
      _runAny.ultraFollowersSpawned = _num(_ultra.follower_spawnedTotal);
      _runAny.ultraFollowersAlive   = Array.isArray(_ultra.follower_list) ? (_ultra.follower_list as unknown[]).length : 0;
      _runAny.ultraTacoDamage       = Math.round(_num(_ultra.taco_damageTotal));
      _runAny.ultraKevinActivations = _num(_ultra.kevin_activations);
      _runAny.ultraDonationProcs    = _num(_ultra.donation_procs);
      _runAny.ultraRageQuitUsed     = _num(_ultra.rageQuit_used);
      _runAny.ultraMomentumProcs    = _num(_ultra.momentum_procs);
      (run as unknown as Record<string, string>).ultraItemsOwned = _activeUltras.join(',');
    }
    // sheeesh / liar / blineTriggers — bot-tracked via the trackItemProc
    // pipeline. Pull the per-item proc counts from getItemState if they
    // exist on the proc tracker; otherwise default to 0.
    try {
      // BETA 20.67 — deep-dive HIGH #6: these four read `_itemProcCounts`, a
      // field NO writer ever populated (two reads repo-wide, zero writes) —
      // all four reported 0 forever, making the items look dead. Real counts
      // live in weaponLab's shared item tracker via trackItemProc (the same
      // source the Iranian-Bird/Whoopie block below already uses).
      const _procTracker = _getLocalItemTrackerForLive();
      _runAny.sheeeshTriggered       = _procTracker['item_sheeesh']?.procCount ?? 0;
      _runAny.liarTriggered          = _procTracker['item_liar_deceiver_false_prophet']?.procCount ?? 0;
      _runAny.timmyRepellentProcs    = _procTracker['item_timmy_repellent_spray']?.procCount ?? 0;
      _runAny.stayFrostySlowsApplied = _procTracker['item_stay_frosty']?.procCount ?? 0;
    } catch { /* never crash on telemetry */ }
    // BETA 17.06 — PSBOT cycle-30v parity gap: groundEffectSpawnSources
    // was bot-only. Now surfaced to live_play from the shared
    // _localFloorEffectTracker that engine.ts:326 registers — same
    // source autoPlaytester reads. Pipe-list format matches bot CSV.
    try {
      const _floorTracker = _getLocalFloorEffectTrackerForLivePlay();
      const _sources = Object.entries(_floorTracker)
        .map(([weaponId, entry]) => `${weaponId}:${(entry as { spawns?: number }).spawns ?? 0}`)
        .filter(s => !s.endsWith(':0'));
      (run as unknown as { groundEffectSpawnSources?: string[] }).groundEffectSpawnSources = _sources;
    } catch { /* never crash on telemetry */ }
    // BETA 17.07 — Karen Drone diagnostic counter (PSBOT cycle-30w Protocol B).
    try {
      const _karen = _getKarenDroneTraceForLivePlay();
      const _runForKaren = run as unknown as Record<string, number>;
      _runForKaren.karenPeakDroneCount = _karen.peakDroneCount;
      _runForKaren.karenContacts       = _karen.contacts;
      _runForKaren.karenCooldownSkips  = _karen.cooldownSkips;
      _runForKaren.karenRadiusSkips    = _karen.radiusSkips;
      _runForKaren.karenLosSkips       = _karen.losSkips;
    } catch { /* never crash on telemetry */ }
    // BETA 17.08 — PSBOT cycle-30t Tier C closing batch (6 final counters).
    // 4 surface from the shared _localItemTracker (procs + dmg already
    // captured by existing trackItemProc/trackItemDmg calls at apply sites).
    // 2 use new state/module fields (anusRocket fire counter, sillyJuice peak).
    try {
      const _itemTracker = _getLocalItemTrackerForLive();
      const _runForItems = run as unknown as Record<string, number>;
      // Iranian Bird
      _runForItems.iranianBirdShots  = _itemTracker['item_iranian_bird']?.procCount ?? 0;
      _runForItems.iranianBirdDamage = Math.round(_itemTracker['item_iranian_bird']?.dmgDealt ?? 0);
      // Whoopie Cushion
      _runForItems.whoopieStunsApplied = _itemTracker['item_whoopie_cushion_of_doom']?.procCount ?? 0;
      // Bungulators
      _runForItems.bungulatorsPunches = _itemTracker['item_bungulators']?.procCount ?? 0;
      _runForItems.bungulatorsDamage  = Math.round(_itemTracker['item_bungulators']?.dmgDealt ?? 0);
      // Anus Rocket — fire count from module + damage from shared tracker
      _runForItems.anusRocketsFired = _getAnusRocketsForLive();
      _runForItems.anusRocketDamage = Math.round(_itemTracker['R08']?.dmgDealt ?? 0);
      // Drac's Gas Mask overheal (sum of shield gained from poisoned-hit overheal)
      _runForItems.dracsGasMaskOverheal = Math.round(_sAnyShield._dracsGasMaskOverhealThisRun ?? 0);
      // Silly Juice peak stack reached this run
      _runForItems.sillyJuiceMaxStack = _getSillyJuicePeakForLive();
    } catch { /* never crash on telemetry */ }
    run.finalXpMultiplier = Math.round((p.xpMultiplier ?? 1) * 100) / 100;
    run.finalGoldMultiplier = Math.round((p.goldMultiplier ?? 1) * 100) / 100;

    // BETA 16.21 — PSBOT cycle-24 P1.1: investigation breadcrumbs for
    // finalMaxHP=85 (below base 100) bug. Captures sources of maxHp at
    // finalize time so PSBOT can see whether the issue is item subtraction,
    // perc misapply, or a snapshot-mid-death timing bug.
    try {
      const itemState = getItemState();
      const percState = getPercState();
      const itemMaxHpStat = itemState?.playerStats?.maxHp ?? null;
      const itemBaseMaxHp = itemState?.playerStats?.baseMaxHp ?? null;
      // Sum perc T03/N04/N12-like maxHp percent contribution from accumulatedBonus.
      let percMaxHpAccum = 0;
      if (percState?.slots) {
        for (const slot of percState.slots) {
          if (slot.perc && slot.perc.stat === 'maxHp' && slot.level > 0) {
            percMaxHpAccum += slot.accumulatedBonus;
          }
        }
      }
      run.finalMaxHpDebug = {
        playerMaxHp: Math.round(p.maxHp ?? 0),
        playerHp: Math.round(p.hp ?? 0),
        itemMaxHpStat,
        itemBaseMaxHp,
        percMaxHpAccum: Math.round(percMaxHpAccum * 100) / 100,
        characterId: state.character?.id ?? 'unknown',
        // Inventory items that touch maxHp directly — easy correlation.
        suspectItems: itemState?.inventory
          ?.filter(s => /iranian|sheeesh|gas_mask|deck/.test(s.item.id))
          .map(s => `${s.item.id}x${s.stacks}`) ?? [],
      };
    } catch { /* never crash on debug */ }
  }

  // BETA 16.29 — PSBOT cycle-30f schema parity: combat-outcome accounting,
  // event counters, and spawn timings carried over from bot_run. All
  // these fields are PLAYER-RELEVANT (not bot-AI-specific) and live on
  // GameState already (engine increments them). Just need to read at
  // finalize time.
  try {
    const sAny = state as unknown as {
      _runGoldSpent?: number;
      _firstBloodTime?: number;
      _firstDamageTakenTime?: number;
      _enemyAttacksAttemptedThisRun?: number;
      _enemyAttacksHitThisRun?: number;
      _enemyAttacksMissedByEvasion?: number;
      _enemyAttacksMissedByIframe?: number;
      _enemyAttacksMissedByPhase?: number;
      _enemyAttacksMissedByOther?: number;
      _enemySpriteOverlapFrames?: number;      // BETA 20.34 — swarm-pressure
      _enemySpriteOnlyTouchFrames?: number;    // telemetry (now in live payload)
      _enemyTouchCooldownGated?: number;
      _reviveEventsThisRun?: number;
      _healEventsThisRun?: number;
      _dodgeEventsThisRun?: number;
      _phaseEventsThisRun?: number;
      _longestStretchBetweenHitsMs?: number;
      _peakAliveCount?: number;
      _aliveSamplesPerMinute?: number[]; // BETA 19.39 — kept for back-compat (no longer written)
      // BETA 19.93 — samples10s expanded to capture every player stat
      // each 10s so post-run analysis can spot mid-run stat bleeds.
      _samples10s?: Array<Record<string, number>>;
    };
    // Run-event counters (player can read these to see "how clean was my
    // run" — fewer enemyAttacksHit + more dodges = better play).
    run.runGoldSpent = sAny._runGoldSpent ?? 0;
    run.firstBloodTime = sAny._firstBloodTime ?? 0;
    run.firstDamageTakenTime = sAny._firstDamageTakenTime ?? 0;
    run.enemyAttacksAttempted = sAny._enemyAttacksAttemptedThisRun ?? 0;
    run.enemyAttacksHit = sAny._enemyAttacksHitThisRun ?? 0;
    run.enemyAttacksMissedByEvasion = sAny._enemyAttacksMissedByEvasion ?? 0;
    run.enemyAttacksMissedByIframe = sAny._enemyAttacksMissedByIframe ?? 0;
    run.enemyAttacksMissedByPhase = sAny._enemyAttacksMissedByPhase ?? 0;
    run.enemyAttacksMissedByOther = sAny._enemyAttacksMissedByOther ?? 0;
    // BETA 20.34 — swarm-pressure telemetry. enemyAttacksAttempted only counts
    // cooldown-cleared contact SWINGS, so a kiting build shows a tiny number that
    // LOOKS broken but isn't. These 3 (written in enemySystem.ts, previously
    // dropped from the live payload) capture how crowded the player actually was.
    run.enemySpriteOverlapFrames = sAny._enemySpriteOverlapFrames ?? 0;
    run.enemySpriteOnlyTouchFrames = sAny._enemySpriteOnlyTouchFrames ?? 0;
    run.enemyTouchCooldownGated = sAny._enemyTouchCooldownGated ?? 0;
    run.reviveEventsThisRun = sAny._reviveEventsThisRun ?? 0;
    run.healEventsThisRun = sAny._healEventsThisRun ?? 0;
    run.dodgeEventsThisRun = sAny._dodgeEventsThisRun ?? 0;
    run.phaseEventsThisRun = sAny._phaseEventsThisRun ?? 0;
    run.longestStretchBetweenHitsMs = sAny._longestStretchBetweenHitsMs ?? 0;
    run.peakAliveCount = sAny._peakAliveCount ?? 0;
    // BETA 19.40 — 10-second granular telemetry replaces aliveSamplesPerMinute.
    // Each sample = { t (run sec), alive, hp, dmgTaken (cumulative) }.
    // 10-min run = 60 samples = ~3KB JSON, well under the description cap.
    // Lets us trace WHEN death cascades start, not just whether they did.
    run.samples10s = sAny._samples10s ?? [];
    // Keep aliveSamplesPerMinute for back-compat (always empty now —
    // the field name is referenced by older PSBOT analysis scripts).
    run.aliveSamplesPerMinute = sAny._aliveSamplesPerMinute ?? [];
  } catch { /* never crash on event counters */ }

  // BETA 16.29 — Character passive layer-1 + signature trackers. Same
  // data the bot finalize already reads. Direct module import (no
  // circular dep — characterPassives doesn't import livePlayTracker).
  try {
    run.characterPassiveLayer1Final = getPassiveBonusFinalThisRun(state);
    run.signatureProcsThisRun = getSignatureProcsThisRun(state);
    run.signatureMaxStacks = getSignatureMaxStacksThisRun(state);
    run.signatureActiveSeconds = getSignatureActiveSecondsThisRun(state);
    run.signatureBonusDamage = getSignatureBonusDamageThisRun(state);
  } catch {
    run.characterPassiveLayer1Final = 0;
    run.signatureProcsThisRun = 0;
    run.signatureMaxStacks = 0;
    run.signatureActiveSeconds = 0;
    run.signatureBonusDamage = 0;
  }

  // ── Weapons + per-weapon damage ──
  try {
    const ws = getWeaponSystemState();
    if (ws) {
      run.weaponsUsed = ws.weapons.map(w => w.definitionId);
      // BETA 20.43 — bounce-event telemetry (Dylan: "track every bounce + if a
      // bounce should have happened and didn't"). bouncesPerformed = successful
      // chains; bounceMissedNoTarget = had bounces left but no enemy in range.
      const _wsBounce = ws as unknown as { bouncesPerformed?: number; bounceMissedNoTarget?: number };
      (run as unknown as Record<string, number>).bouncesPerformed = _wsBounce.bouncesPerformed ?? 0;
      (run as unknown as Record<string, number>).bounceMissedNoTarget = _wsBounce.bounceMissedNoTarget ?? 0;
      run.weaponTelemetry = ws.weapons.map(w => {
        const dmg = Math.round((w as unknown as { totalDamageDealt?: number }).totalDamageDealt ?? 0);
        const fired = (w as unknown as { totalShotsFired?: number }).totalShotsFired ?? 0;
        const hit = (w as unknown as { totalShotsHit?: number }).totalShotsHit ?? 0;
        const crits = (w as unknown as { totalCrits?: number }).totalCrits ?? 0;
        // BETA 16.20 — PSBOT cycle-23 P1.2: orbital-contact weapons (Karen
        // Drones) don't fire projectiles, so totalShotsFired stays 0 and
        // PSBOT saw "wpn_karen_drones:lvl1,hit=36/0(0%)". The weapon DOES
        // hit (36 hits, 5 crits, 1811 dmg) — accuracy math just used the
        // wrong divisor. Use totalTickEvents as fallback when totalShotsFired
        // is 0 (orbital contact + aura weapons increment tickEvents per
        // active frame). Rendering becomes "hit=36/N(P%),crit=5" where
        // N = tickEvents = ticks-during-which-weapon-was-active.
        const ticks = (w as unknown as { totalTickEvents?: number }).totalTickEvents ?? 0;
        const denom = fired > 0 ? fired : ticks;
        const acc = denom > 0 ? Math.round((hit / denom) * 100) : 0;
        return `${w.definitionId}:lvl${w.level},dmg=${dmg},hit=${hit}/${denom}(${acc}%),crit=${crits}`;
      });
      run.totalDamageDealt = Math.round((ws as unknown as { totalDamageDealt?: number }).totalDamageDealt ?? 0);
      run.damageDealt = run.totalDamageDealt; // alias to match RunLog shape
    } else {
      run.weaponsUsed = [];
      run.weaponTelemetry = [];
      run.totalDamageDealt = 0;
      run.damageDealt = 0;
    }
  } catch {
    run.weaponsUsed = [];
    run.weaponTelemetry = [];
    run.totalDamageDealt = 0;
    run.damageDealt = 0;
  }

  // ── Items + percs ──
  // BETA 16.20 — PSBOT cycle-23 P1.5 confirmed these were empty across all
  // runs because the readers were looking for fields that don't exist
  // (`owned` on BullshiiiiSystemState, `ownedPercs` on PeanutPercRunState).
  // Real shapes: BullshiiiiSystemState.inventory: InventorySlot[]
  // (`{item: {id}, stacks: number}`) and PeanutPercRunState.slots:
  // PercSlot[] (`{perc: {id} | null, level: number}`). Fixed.
  try {
    const items = getItemState();
    if (items && Array.isArray(items.inventory)) {
      run.itemsCollected = items.inventory.map(slot =>
        slot.stacks > 1 ? `${slot.item.id} x${slot.stacks}` : slot.item.id
      );
    } else {
      run.itemsCollected = [];
    }
  } catch {
    run.itemsCollected = [];
  }

  try {
    const percs = getPercState();
    if (percs && Array.isArray(percs.slots)) {
      // Bot CSV uses percLevels as pipe-list "T01:5|T03:3" — match that shape.
      run.percLevels = percs.slots
        .filter(s => s.perc && s.level > 0)
        .map(s => `${s.perc!.id}:${s.level}`);
    } else {
      run.percLevels = [];
    }
  } catch {
    run.percLevels = [];
  }

  // BETA 16.20 — PSBOT cycle-23 P1.5: pickEvents log. Per PSBOT: "without
  // itemsCollected and percLevels we can't validate the offer-cycling
  // fix, diagnose stuck stats, track adoption rates, or compare bot vs
  // human pick patterns." Lightweight per-pick log captured live by
  // recordLivePickEvent (called from itemsAdapter pick paths). Schema:
  //   { t, source, pickedId, rarity, stat?, delta? }
  run.pickEvents = _pickEvents.slice();

  // BETA 16.70 — Per Dylan: "would you be able to set up mega supa base
  // tracking for all cellphone play throughs?"
  // BETA 16.71 — Per Dylan (extended): "we need to know what is working
  // and what isnt working and it is all trackable. controller too." Now
  // tags EVERY run (mobile or desktop or handheld) with device + input-mix
  // + controller + per-run perf stats so PSBOT can audit any platform.
  try {
    const w = typeof window !== 'undefined' ? window : null;
    const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
    run.deviceClass = classifyDevice();
    run.isTouch = isTouchDevice();
    run.isStandalonePWA = isStandalonePWA();
    run.isIPhoneSafari = isIPhoneSafari();
    run.userAgent = ua.slice(0, 200); // cap length for DB sanity
    run.viewportWidth = w?.innerWidth ?? 0;
    run.viewportHeight = w?.innerHeight ?? 0;
    run.devicePixelRatio = w?.devicePixelRatio ?? 1;
    run.orientation = (w && w.innerHeight > w.innerWidth) ? 'portrait' : 'landscape';
    run.networkClass = getNetworkClass();
    run.hardwareCores = (typeof navigator !== 'undefined' ? navigator.hardwareConcurrency : 0) ?? 0;
    const memGB = (typeof navigator !== 'undefined'
      ? (navigator as unknown as { deviceMemory?: number }).deviceMemory
      : undefined);
    run.deviceMemoryGB = memGB ?? 0;
    // Save-data flag: useful for spotting low-bandwidth users who may
    // hit asset-loading bugs. From navigator.connection.saveData.
    const conn = (typeof navigator !== 'undefined'
      ? (navigator as unknown as { connection?: { saveData?: boolean } }).connection
      : undefined);
    run.saveDataMode = conn?.saveData === true;

    // BETA 16.71 — Input mix telemetry. % of run-time each source was
    // active so PSBOT can spot input-mode-specific patterns:
    // "controller players survive 1.5x as long" / "all our hangs hit
    // touch users" / etc.
    const inputMix = getInputMixSnapshot();
    run.inputPrimary = inputMix.primary;        // 'keyboard' | 'mouse' | 'touch' | 'gamepad' | 'mixed' | 'idle'
    run.inputKeyboardSec = inputMix.keyboardSec;
    run.inputMouseSec = inputMix.mouseSec;
    run.inputTouchSec = inputMix.touchSec;
    run.inputGamepadSec = inputMix.gamepadSec;
    run.inputKeyboardPct = inputMix.keyboardPct;
    run.inputMousePct = inputMix.mousePct;
    run.inputTouchPct = inputMix.touchPct;
    run.inputGamepadPct = inputMix.gamepadPct;

    // BETA 16.71 — Connected controller (if any). Captured at run end
    // so a player who plugged in mid-run still gets the device name.
    const controller = getConnectedControllerName();
    run.controllerConnected = controller !== null;
    run.controllerName = controller ? controller.slice(0, 100) : null;

    // BETA 16.71 — Per-run perf stats (avgFps, worstFps, droppedFrames,
    // totalFrames). Lets PSBOT spot platform-specific perf regressions
    // (e.g. iPhone Air avg 28fps after BETA 16.69, ROG Ally 60fps).
    const perf = getPerfRunSnapshot();
    run.perfAvgFps = perf.avgFps;
    run.perfWorstFps = perf.worstFps;
    run.perfWorstFrameMs = perf.worstFrameMs;
    run.perfDroppedFrames = perf.droppedFrames;
    run.perfTotalFrames = perf.totalFrames;
  } catch {
    // Don't let telemetry collection crash a run submit.
  }

  // BETA 22.47 — NEW-SYSTEM fields (dash / mods / Cloakzy / extraction stash).
  // buildLiveRun inline-builds the row (doesn't call buildExtendedTelemetryFields),
  // so copy the 6 new fields here for human/bot parity.
  try {
    const ext = buildExtendedTelemetryFields(state);
    run.dashesUsed = ext.dashesUsed;
    run.cloakzyDeals = ext.cloakzyDeals;
    run.cloakzyGoldSpent = ext.cloakzyGoldSpent;
    run.modsSocketedAtEnd = ext.modsSocketedAtEnd;
    run.stashScrap = ext.stashScrap;
    run.stashCases = ext.stashCases;
  } catch { /* never block a submit */ }

  return run;
}

/**
 * Public: call from engine.ts at the moment the run ends.
 *
 * Idempotent within a single run — call site sets a guard flag so we don't
 * double-submit when both `state.phase = 'gameover'` and a subsequent
 * back-to-menu transition fire in quick succession.
 *
 * Routes through recordBotRunEvent which:
 *   - writes to localStorage backup (LOCAL_RUNS_KEY)
 *   - posts to Supabase via setTimeout(0) (never blocks frame)
 *   - sets category='live_play' + reporter='live-play:<username>' in
 *     perfTelemetry.sendBotRunToSupabase based on isBotFarm flag.
 */
let _alreadySubmittedThisRun = false;

export function finalizeLiveRun(state: GameState, outcome: LiveOutcome): void {
  if (_alreadySubmittedThisRun) return;
  _alreadySubmittedThisRun = true;
  try {
    const run = buildLiveRun(state, outcome);
    recordBotRunEvent(run, 'RUN');
  } catch {
    // Never let telemetry crash the death/win flow.
  }
}

/**
 * Reset the once-per-run guard. Call when a new run starts (engine init,
 * loadout selection, character pick, etc.) so the NEXT death/win submits.
 * BETA 16.20 — also resets the per-pick event log.
 */
export function resetLivePlayTracker(): void {
  _alreadySubmittedThisRun = false;
  _pickEvents = [];
  // BETA 16.24 — clear captured hpAtDeath so REPLAY starts a fresh capture.
  _capturedHpAtDeath = null;
}
